import { cn, titleCase } from "@/lib/utils.ts";

export type StatusTone = "neutral" | "info" | "success" | "warning" | "destructive" | "muted";

export interface StatusMeta {
  tone: StatusTone;
  label: string;
  pulse?: boolean;
}

const STATUS: Record<string, StatusMeta> = {
  received: { tone: "info", label: "Received" },
  validated: { tone: "info", label: "Validated" },
  mapped: { tone: "info", label: "Mapped" },
  held: { tone: "warning", label: "Held" },
  queued: { tone: "info", label: "Queued", pulse: true },
  delivering: { tone: "info", label: "Delivering", pulse: true },
  delivered: { tone: "success", label: "Delivered" },
  acknowledged: { tone: "success", label: "Acknowledged" },
  rejected: { tone: "destructive", label: "Rejected" },
  failed: { tone: "destructive", label: "Failed" },
  invalid: { tone: "destructive", label: "Invalid" },
  duplicate: { tone: "muted", label: "Duplicate" },
  quarantined: { tone: "warning", label: "Quarantined" },
  cancelled: { tone: "muted", label: "Cancelled" },
  purged: { tone: "muted", label: "Purged" },
  archived: { tone: "muted", label: "Archived" },
  draft: { tone: "neutral", label: "Draft" },
  packed: { tone: "info", label: "Packed" },
  labelled: { tone: "info", label: "Labelled" },
  sent: { tone: "success", label: "Sent" },
  delivery_failed: { tone: "destructive", label: "Delivery failed" },
  superseded: { tone: "muted", label: "Superseded" },
  pending: { tone: "warning", label: "Pending", pulse: true },
  skipped: { tone: "muted", label: "Skipped" },
  accepted: { tone: "success", label: "Accepted" },
  accepted_with_errors: { tone: "warning", label: "Accepted with errors" },
  active: { tone: "success", label: "Active" },
  enabled: { tone: "success", label: "Enabled" },
  disabled: { tone: "muted", label: "Disabled" },
  suspended: { tone: "warning", label: "Suspended" },
  onboarding: { tone: "info", label: "Onboarding" },
  paused: { tone: "warning", label: "Paused" },
  running: { tone: "info", label: "Running", pulse: true },
  ok: { tone: "success", label: "OK" },
  warn: { tone: "warning", label: "Warning" },
  warning: { tone: "warning", label: "Warning" },
  error: { tone: "destructive", label: "Error" },
  critical: { tone: "destructive", label: "Critical" },
  info: { tone: "info", label: "Info" },
  block: { tone: "destructive", label: "Blocked" },
  read: { tone: "muted", label: "Read" },
  unread: { tone: "info", label: "Unread" },
};

export function statusMeta(status: string | null | undefined): StatusMeta {
  if (!status) return { tone: "muted", label: "—" };
  return STATUS[status.toLowerCase()] ?? { tone: "neutral", label: titleCase(status) };
}

const toneClass: Record<StatusTone, { pill: string; dot: string }> = {
  neutral: { pill: "border-border bg-muted/60 text-foreground/80", dot: "bg-foreground/50" },
  muted: { pill: "border-border bg-muted/40 text-muted-foreground", dot: "bg-muted-foreground/60" },
  info: { pill: "border-info/25 bg-info/10 text-info", dot: "bg-info" },
  success: { pill: "border-success/25 bg-success/10 text-success", dot: "bg-success" },
  warning: { pill: "border-warning/35 bg-warning/15 text-[oklch(0.5_0.13_75)] dark:text-warning", dot: "bg-warning" },
  destructive: { pill: "border-destructive/25 bg-destructive/10 text-destructive", dot: "bg-destructive" },
};

export interface StatusPillProps extends Omit<React.ComponentProps<"span">, "children"> {
  status: string | null | undefined;
  label?: string;
  size?: "sm" | "default";
  dot?: boolean;
}

export function StatusPill({ status, label, size = "default", dot = true, className, ...props }: StatusPillProps) {
  const meta = statusMeta(status);
  const t = toneClass[meta.tone];
  return (
    <span
      data-slot="status-pill"
      data-status={status ?? undefined}
      data-tone={meta.tone}
      className={cn("inline-flex w-fit shrink-0 items-center gap-1.5 rounded-full border font-medium whitespace-nowrap", size === "sm" ? "h-5 px-1.5 text-[11px]" : "h-6 px-2 text-xs", t.pill, className)}
      {...props}
    >
      {dot && (
        <span className="relative flex size-1.5" aria-hidden="true">
          {meta.pulse && <span className={cn("absolute inline-flex size-full animate-ping rounded-full opacity-60", t.dot)} />}
          <span className={cn("relative inline-flex size-1.5 rounded-full", t.dot)} />
        </span>
      )}
      {label ?? meta.label}
    </span>
  );
}

export default StatusPill;
